import cn from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import applyImageScaleDownOperation from 'editor/src/store/design/operation/applyImageScaleDownOperation';
import requestImageUpscaleOperation from 'editor/src/store/design/operation/requestImageUpscaleOperation';
import getCurrentDpiLevels from 'editor/src/store/design/selector/getCurrentDpiLevels';
import { MediaElement } from 'editor/src/store/design/types';
import setSidebarActiveTabByNameOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabByNameOperation';
import hasSidebarTab from 'editor/src/store/editorModules/sidebar/selector/hasSidebarTab';
import { TAB_NAMES } from 'editor/src/store/editorModules/sidebar/types';
import { DPIWarning, WarningLevel } from 'editor/src/store/editorModules/warnings/types';
import getImageById from 'editor/src/store/gallery/selector/getImageById';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import { DEFAULT_MEDIUM_DPI } from 'editor/src/store/hostSettings/slice';
import { RootState } from 'editor/src/store/index';
import { getImageManipulationPluginData } from 'editor/src/store/plugins/utils/imageManipulationUtils';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import useCustomToastr from 'editor/src/component/CustomToastrComponent/useCustomToastr';
import IconExclamationEmpty from 'editor/src/component/Icon/IconExclamationEmpty';
import LinkButton from 'editor/src/component/LinkButton';
import { getDPIWarningTooltip } from 'editor/src/component/TopMenuDesktop/buttons/ButtonWarnings/useElementWarnings';
import WithTooltip from 'editor/src/component/WithTooltip';

import AbstractWarningItem from './AbstractWarningItem';

import styles from './index.module.scss';

const GPU_PIXEL_LIMIT = 2096704;

const isImageTooLargeForUpscale = (state: RootState, element: MediaElement): boolean => {
  if (element.type !== 'image' || !element.imageId) {
    return false;
  }
  // Get the source image dimensions (what the API will process)
  const galleryImage = getImageById(state, element.imageId);
  if (!galleryImage) {
    return false;
  }
  const pixelCount = galleryImage.width * galleryImage.height;
  return pixelCount > GPU_PIXEL_LIMIT;
};

interface Props {
  warning: DPIWarning;
  element: MediaElement;
  selected: boolean;
}

function LowDpiWarningItem({ warning, element, selected }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showToastr = useCustomToastr();
  const dpiLevelsSettings = useSelector(getCurrentDpiLevels, shallowEqual);
  const isLowDpi = warning.level === WarningLevel.High;
  const title = isEmbedded ? t('Low quality image') : getDPIWarningTooltip(warning.dpi, warning.level, t);
  const message = useMemo(() => {
    if (isLowDpi) {
      return t('editor-poor-quality-info', { dpi: Math.round(dpiLevelsSettings?.low ?? DEFAULT_MEDIUM_DPI) });
    }
    if (!isEmbedded) {
      return t('editor-average-quality-info', { dpi: Math.round(dpiLevelsSettings?.medium ?? DEFAULT_MEDIUM_DPI) });
    }
    return t('Low quality image message');
  }, [warning.level, dpiLevelsSettings, isLowDpi]);

  const { isGalleryTabActive, dpiLimits, state } = useSelector((state) => ({
    isGalleryTabActive: hasSidebarTab(state, TAB_NAMES.GALLERY),
    dpiLimits: getCurrentDpiLevels(state),
    state,
  }));

  const activateFilesTab = () => {
    dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.GALLERY));
  };

  const resizeLowDpiImage = () => {
    const scale = warning.dpi / (dpiLimits.low + 1);
    dispatch(applyImageScaleDownOperation(element, scale));

    showToastr('success', t('Image display size adjusted for better print quality'), { timeOut: 5000 });
  };

  const improveResolution = () => {
    void dispatch(requestImageUpscaleOperation(element));
  };

  // Check if image has already been auto-enhanced
  const isImageAutoEnhanced =
    element.type === 'image' &&
    (getImageManipulationPluginData('app-image-upscale', element) ||
      getImageManipulationPluginData('image-enhancement', element));

  const isImageTooLarge = isImageTooLargeForUpscale(state, element);

  return (
    <AbstractWarningItem warning={warning} element={element} selected={selected} title={title}>
      {message}
      {isLowDpi && (
        <>
          {(() => {
            if (!isImageAutoEnhanced && !isImageTooLarge) {
              return (
                <LinkButton
                  className={cn('cy-improve-resolution', styles.actionLinkButton)}
                  black={false}
                  onClick={improveResolution}
                >
                  {t('Auto-enhance image')}
                </LinkButton>
              );
            }
            if (isImageTooLarge) {
              return (
                <div className={styles.disabledHint}>
                  <div style={{ opacity: 0.6, textDecoration: 'line-through' }}>{t('Auto-enhance image')}</div>
                  <WithTooltip overlay={<div style={{ textAlign: 'left' }}>{t('Auto-enhancement limits')}</div>}>
                    <IconExclamationEmpty className={styles.disabledHintIcon} />
                  </WithTooltip>
                </div>
              );
            }
            return <div className={styles.enhancedHint}>{t('Image has been auto-enhanced')}</div>;
          })()}
          <LinkButton
            className={cn('cy-scale-down-image', styles.actionLinkButton)}
            black={false}
            onClick={resizeLowDpiImage}
          >
            {t('Scale image down')}
          </LinkButton>
          {isGalleryTabActive && (
            <LinkButton
              className={cn('cy-choose-different-image', styles.actionLinkButton)}
              black={false}
              onClick={activateFilesTab}
            >
              {t('Choose different image')}
            </LinkButton>
          )}
        </>
      )}
    </AbstractWarningItem>
  );
}

export default React.memo(LowDpiWarningItem);
